import React, { useContext, useState } from 'react';
import { useMutation } from '@apollo/react-hooks';
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import { useAlert } from 'react-alert';

import { CredentialContext, credentialType } from '@providers';

import Button from '@src/componentsNew/Buttons/Button';
import FacebookIcon from '@src/componentsNew/Icons/Icons/FacebookIcon';

import { AuthorizationKeyType } from '__generated__/globalTypes';
import { OAUTH_TOKEN_CREATE } from '@src/componentsNew/Buttons/SocialLoginButton/queries';
import { oAuthTokenCreate } from '@src/componentsNew/Buttons/SocialLoginButton/__generated__/oAuthTokenCreate';
import { CUSTOMER_UPDATE } from '@components/pages/ContactPage/queries';
import { customerUpdate } from '@components/pages/ContactPage/__generated__/customerUpdate';

import { isWithinMinutes } from '@src/utils';
import { gaV4Login, gaV4SignUp, getDBIdFromGraphqlId, getGraphqlIdFromDBId } from '@utils';
import { OAUTH_LOGIN_EMPTY_EMAIL_ALERT_MESSAGE } from '@src/utils/constants';

interface IProps {
  buttonDisabled?: boolean;
  onCheckBeforeClick?: any;
}

const FacebookLoginButton = ({ buttonDisabled = false, onCheckBeforeClick }: IProps) => {
  const alert = useAlert();
  const { setCurrentUser, anonymousCart, mgsAnonymousCart, mergeCart } = useContext<credentialType>(CredentialContext);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const [updateCustomer] = useMutation<customerUpdate>(CUSTOMER_UPDATE);

  const [oauthTokenCreate] = useMutation<oAuthTokenCreate>(OAUTH_TOKEN_CREATE, {
    onCompleted: async (data) => {
      if (!data?.oauthTokenCreate?.user.email) {
        alert.error(OAUTH_LOGIN_EMPTY_EMAIL_ALERT_MESSAGE);

        return;
      }

      const errors = data?.oauthTokenCreate?.errors;

      if (!errors?.length) {
        const currentUserObject = data?.oauthTokenCreate;
        //@ts-ignore
        setCurrentUser(currentUserObject);
        //merge cart request
        if (anonymousCart && mgsAnonymousCart) {
          const maccaronAnonymousCartId = getGraphqlIdFromDBId(anonymousCart.token, 'Cart');
          const mgsAnonymousCartId = getGraphqlIdFromDBId(mgsAnonymousCart.token, 'Cart');

          await mergeCart({ variables: { cartId: maccaronAnonymousCartId } });
          await mergeCart({ variables: { cartId: mgsAnonymousCartId } });

          await updateCustomer({
            variables: {
              input: {
                firstName: data?.oauthTokenCreate?.user?.firstName,
                lastName: data?.oauthTokenCreate?.user?.lastName,
              },
            },
          });
        }
      } else {
        alert.error(errors[0].message);
      }
    },
  });

  // Facebook
  const responseFacebook = (response) => {
    if (response?.status === 'unknown') {
      return;
    }

    if (!response?.email) {
      alert.error(OAUTH_LOGIN_EMPTY_EMAIL_ALERT_MESSAGE);
      return;
    }

    if (response?.name) {
      const name = response.name;
      const names = name.split(' ');
      if (0 in names) {
        setFirstName(names[0]);
      }
      if (1 in names) {
        setLastName(names[1]);
      }
    }
    if (response?.accessToken) {
      oauthTokenCreate({
        variables: {
          accessToken: response.accessToken,
          backend: AuthorizationKeyType.FACEBOOK,
        },
      }).then((data) => {
        const userId = getDBIdFromGraphqlId(data?.data?.oauthTokenCreate?.user?.id);
        const dateJoined = data?.data?.oauthTokenCreate?.user?.dateJoined;

        if (isWithinMinutes(dateJoined, 5)) {
          gaV4SignUp('Facebook');
        } else {
          gaV4Login('Facebook', userId?.toString());
        }
      });
    } else {
      alert.error("Couldn't log-in from your Facebook account. " + 'Please check your account information again.');
    }
  };

  const handleClickFacebookLoginButton = (renderProps) => {
    if (!buttonDisabled) {
      renderProps.onClick();
    } else {
      onCheckBeforeClick();
    }
  };

  return (
    <FacebookLogin
      appId={process.env.FACE_BOOK_APP_ID}
      autoLoad={false}
      fields="name,email"
      callback={responseFacebook}
      disableMobileRedirect={true}
      render={(renderProps) => (
        <Button onClick={() => handleClickFacebookLoginButton(renderProps)}>
          <FacebookIcon />
        </Button>
      )}
    />
  );
};

export default FacebookLoginButton;
